import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { useMount, useUnmount } from 'react-use';

import { Country, Taxonomy } from 'types/worldTaxonomy';

import styles from './WorldTaxonomy.module.scss';

interface Props {
  taxonomy: Taxonomy[];
  countryComponent: (country: Country) => JSX.Element;
  headerSuffix?: string;
}

const WorldTaxonomy: React.FC<Props> = ({ taxonomy, countryComponent, headerSuffix }) => {
  const [fixContinentsList, setFixContinentsList] = useState(false);
  const [activeContinent, setActiveContinent] = useState<number | null>(null);

  const continentRefs = useRef(new Map<number, React.RefObject<HTMLHeadingElement>>());
  const continentsRef = useRef<HTMLDivElement | null>(null);
  const fixedContinentsRef = useRef<HTMLDivElement | null>(null);

  const containerClass = 'quiver-world-taxonomy__container';

  const checkFixContinentsList = () => {
    if (continentsRef.current) {
      setFixContinentsList(continentsRef.current.getBoundingClientRect().top <= 0);
    }
  };

  const checkActiveContinent = () => {
    const continents = Array.from(continentRefs.current).map(([geonameId, ref]) => ({
      geonameId,
      top: ref.current?.getBoundingClientRect().top || 0,
    }));

    const activeContinentIndex =
      continents.findIndex(({ top }) => top >= (fixedContinentsRef.current?.offsetHeight ?? 0)) - 1;
    const newActiveContinent =
      activeContinentIndex === -2
        ? continents[continents.length - 1].geonameId
        : continents[Math.max(0, activeContinentIndex)].geonameId;

    setActiveContinent(newActiveContinent);
  };

  useMount(() => {
    window.addEventListener('scroll', checkFixContinentsList);
    window.addEventListener('scroll', checkActiveContinent);
    checkFixContinentsList();
    checkActiveContinent();
  });

  useUnmount(() => {
    window.removeEventListener('scroll', checkFixContinentsList);
    window.removeEventListener('scroll', checkActiveContinent);
  });

  const activeContinentClassName = (geonameId: number) =>
    activeContinent === geonameId ? 'quiver-world-taxonomy__continents__active' : '';

  const continentsList = (fixed: boolean) => (
    <ul className={fixed ? containerClass : ''}>
      {taxonomy.map((continent) => (
        <li key={continent.geonameId} className={activeContinentClassName(continent.geonameId)}>
          <a href={`#${continent.slug}`}>{continent.name}</a>
        </li>
      ))}
    </ul>
  );

  return (
    <Box
      className={classNames({
        [styles.worldTaxonomy]: true,
        'quiver-world-taxonomy': true,
        [styles.fixed]: fixContinentsList,
      })}
    >
      <Box className={containerClass}>
        <h1 id="world-taxonomy-title">
          World
          {headerSuffix}
        </h1>
        <Box
          ref={continentsRef}
          className="quiver-world-taxonomy__continents"
          data-testid="quiver-world-taxonomy__continents"
        >
          {continentsList(false)}
        </Box>
        <Box
          ref={fixedContinentsRef}
          className="quiver-world-taxonomy__continents quiver-world-taxonomy__continents--fixed"
          data-testid="quiver-world-taxonomy__continents"
        >
          {continentsList(true)}
        </Box>
      </Box>
      {taxonomy.map((continent) => (
        <Box key={continent.geonameId} className="quiver-world-taxonomy__continent">
          <h2
            ref={(ref) => {
              if (ref !== null) {
                // @ts-ignore
                continentRefs.current.set(continent.geonameId, ref);
              }
            }}
            id={continent.slug}
            className={containerClass}
          >
            {continent.name} {headerSuffix}
          </h2>
          <Box
            className="quiver-world-taxonomy__countries"
            data-testid={`${continent.slug}-countries`}
          >
            <ul className={containerClass}>
              {continent.countries.map((letter) => (
                <li key={letter.key}>
                  <Box className="quiver-world-taxonomy__letter">
                    <h3>{letter.key}</h3>
                    <ul>
                      {letter.countries.map((country) => (
                        <li key={country.geonameId}>{countryComponent(country)}</li>
                      ))}
                    </ul>
                  </Box>
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default WorldTaxonomy;
